import { API_URL } from "./helpers";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAppContext } from "../AppProviders";
import { useWebSocket } from "../services/websocket/WebSocketContext";
import { useWebSocketEvent } from "../services/websocket/useWebSocketEvent";

export interface WebSearchRunRequest {
  report_uuid: string;
  page_depth?: number;
  mode?: "fresh" | "resume_run" | "resume_latest_partial";
  resume_run_id?: number;
}

export interface WebSearchRunResponse {
  run_external_id: string;
  run_id?: number;
  report_id: number;
  executed_rounds: number;
  executed_pages: number;
  created_results: number;
  skipped_banned: number;
  skipped_duplicates: number;
  failed_steps: number;
  failed_step_details: Array<Record<string, any>>;
  sample_errors: string[];
  execution_engine: Record<string, any>;
}

export interface WebSearchJobStatus {
  run_external_id?: string; // May be called external_id in some responses
  external_id?: string; // Backend may return this instead
  run_id?: number;
  id?: number; // Backend may return this instead
  report_id: number;
  status: string;
  provider?: string;
  integration_method?: string;
  finished_at?: string;
  created_at?: string;
  payload?: Record<string, any>;
  progress?: {
    total_steps: number;
    completed_steps: number;
    failed_steps: number;
    running_steps: number;
    created_results: number;
  };
  /** Reason for failure when status is "failed" (e.g. timeout, no results). */
  error_message?: string;
}

export interface WebSearchPiiTextOffset {
  start: number;
  end: number;
}

export interface WebSearchPiiEntityMatch {
  text: string;
  offsets: WebSearchPiiTextOffset[];
}

export interface WebSearchPiiMatchData {
  names: WebSearchPiiEntityMatch[];
  institutions: WebSearchPiiEntityMatch[];
}

export interface WebSearchResult {
  id: number;
  external_id: string;
  job_step_id: number;
  report_id: number;
  result_index: number;
  title: string;
  url?: string;
  author?: string;
  date?: string;
  description?: string;
  body: string;
  relevance_score?: number | null;
  pii_matches?: WebSearchPiiMatchData | null;
  status: "unseen" | "viewed" | "added" | "skipped";
  dedupe_key?: string;
  news_id?: number;
  created_at?: string;
  modified_at?: string;
}

interface WebSearchResultsPage {
  items: WebSearchResult[];
  page: number;
  size: number;
  total: number;
}

interface FetchWebSearchResultsPagesRequest {
  requestApi: {
    get: (
      url: string
    ) => Promise<{ data: WebSearchResult[] | WebSearchResultsPage }>;
  };
  report_id: number;
}

const WEB_SEARCH_RESULTS_PAGE_SIZE = 100;

export interface PromoteResultResponse {
  news: Record<string, any>;
  web_search_result: WebSearchResult;
  message?: string;
}

/**
 * Start a web search run for a report
 */
export function useStartWebSearch() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation(
    async (request: WebSearchRunRequest): Promise<WebSearchRunResponse> => {
      const params = new URLSearchParams();
      params.append("report_uuid", request.report_uuid);
      if (request.page_depth !== undefined) {
        params.append("page_depth", request.page_depth.toString());
      }
      if (request.mode) {
        params.append("mode", request.mode);
      }
      if (request.resume_run_id !== undefined) {
        params.append("resume_run_id", request.resume_run_id.toString());
      }

      // POST with query params (backend expects Query() parameters)
      const response = await requestApi.post(
        `${API_URL}/360/web_search/run?${params.toString()}`,
        {}
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Invalidate job status queries to trigger refetch
        queryClient.invalidateQueries([
          "webSearchJobStatus",
          data.run_external_id,
        ]);
        queryClient.invalidateQueries(["webSearchResults", data.report_id]);
      },
    }
  );
}

export function useWebSearchJobSubscription(run_external_id: string | null) {
  const queryClient = useQueryClient();
  const { connectionState } = useWebSocket();

  useWebSocketEvent<{
    run_external_id: string;
    run_id?: number;
    report_id: number;
    report_uuid: string;
    status: string;
    progress?: {
      total_steps: number;
      completed_steps: number;
      failed_steps: number;
      running_steps: number;
      created_results: number;
    };
    finished_at?: string;
    error?: string;
  }>(
    "job_started",
    (data) => {
      if (data.run_external_id === run_external_id) {
        queryClient.invalidateQueries(["webSearchJobStatus", run_external_id]);
      }
    },
    { enabled: !!run_external_id && connectionState === "connected" }
  );

  useWebSocketEvent<{
    run_external_id: string;
    run_id?: number;
    report_id: number;
    report_uuid: string;
    status: string;
    progress: {
      total_steps: number;
      completed_steps: number;
      failed_steps: number;
      running_steps: number;
      created_results: number;
    };
  }>(
    "job_progress",
    (data) => {
      if (data.run_external_id === run_external_id) {
        queryClient.invalidateQueries(["webSearchJobStatus", run_external_id]);
      }
    },
    { enabled: !!run_external_id && connectionState === "connected" }
  );

  useWebSocketEvent<{
    run_external_id: string;
    run_id?: number;
    report_id: number;
    report_uuid: string;
    status: string;
    progress: {
      total_steps: number;
      completed_steps: number;
      failed_steps: number;
      running_steps: number;
      created_results: number;
    };
    finished_at?: string;
  }>(
    "job_complete",
    (data) => {
      if (data.run_external_id === run_external_id) {
        queryClient.invalidateQueries(["webSearchJobStatus", run_external_id]);
        queryClient.invalidateQueries(["webSearchResults", data.report_id]);
        queryClient.invalidateQueries(["webSearchJobResults", run_external_id]);
      }
    },
    { enabled: !!run_external_id && connectionState === "connected" }
  );

  useWebSocketEvent<{
    run_external_id: string;
    run_id?: number;
    report_id: number;
    report_uuid: string;
    status: string;
    progress: {
      total_steps: number;
      completed_steps: number;
      failed_steps: number;
      running_steps: number;
      created_results: number;
    };
    finished_at?: string;
    error?: string;
  }>(
    "job_failed",
    (data) => {
      if (data.run_external_id === run_external_id) {
        queryClient.invalidateQueries(["webSearchJobStatus", run_external_id]);
      }
    },
    { enabled: !!run_external_id && connectionState === "connected" }
  );
}

/**
 * Get the latest web search job for a report (if any exists).
 * Returns the most recent job with status 'running' or 'partial', or the most recent job overall.
 */
export function useLatestWebSearchJobForReport(
  report_uuid: string | null,
  enabled: boolean = true
) {
  const { requestApi } = useAppContext();

  return useQuery<WebSearchJobStatus | null>(
    ["latestWebSearchJobForReport", report_uuid],
    async () => {
      if (!report_uuid) {
        throw new Error("report_uuid is required");
      }
      const response = await requestApi.get(
        `${API_URL}/360/web_search/jobs/report/${report_uuid}`
      );
      return response.data || null;
    },
    {
      enabled: enabled && !!report_uuid,
      retry: (failureCount, error: any) => {
        if (
          error?.response?.status === 404 ||
          error?.response?.status === 502
        ) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: 1000,
    }
  );
}

export function useWebSearchJobStatus(
  run_external_id: string | null,
  enabled: boolean = true
) {
  const { requestApi } = useAppContext();
  const { connectionState } = useWebSocket();
  const isWebSocketConnected = connectionState === "connected";

  useWebSearchJobSubscription(run_external_id);

  return useQuery<WebSearchJobStatus>(
    ["webSearchJobStatus", run_external_id],
    async () => {
      if (!run_external_id) {
        throw new Error("run_external_id is required");
      }
      const response = await requestApi.get(
        `${API_URL}/360/web_search/jobs/${run_external_id}`
      );
      return response.data;
    },
    {
      enabled: enabled && !!run_external_id,
      refetchInterval: (data) => {
        if (isWebSocketConnected) {
          return false;
        }
        const status = data?.status?.toLowerCase();
        if (status === "running" || status === "partial") {
          return 5000;
        }
        return false;
      },
      retry: (failureCount, error: any) => {
        if (
          error?.response?.status === 404 ||
          error?.response?.status === 502
        ) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: 1000,
    }
  );
}

export function useWebSearchJobResults(
  run_external_id: string | null,
  enabled: boolean = true
) {
  const { requestApi } = useAppContext();

  return useQuery<WebSearchResult[]>(
    ["webSearchJobResults", run_external_id],
    async () => {
      if (!run_external_id) {
        throw new Error("run_external_id is required");
      }
      const response = await requestApi.get(
        `${API_URL}/360/web_search/jobs/${run_external_id}/results`
      );
      return response.data;
    },
    {
      enabled: enabled && !!run_external_id,
    }
  );
}

export function useWebSearchResults(
  report_id: number | null,
  enabled: boolean = true
) {
  const { requestApi } = useAppContext();

  return useQuery<WebSearchResult[]>(
    ["webSearchResults", report_id],
    async () => {
      if (!report_id) {
        throw new Error("report_id is required");
      }
      return fetchWebSearchResultsPages({ requestApi, report_id });
    },
    {
      enabled: enabled && !!report_id,
    }
  );
}

async function fetchWebSearchResultsPages({
  requestApi,
  report_id,
}: FetchWebSearchResultsPagesRequest): Promise<WebSearchResult[]> {
  const results: WebSearchResult[] = [];
  let page = 1;

  while (true) {
    const params = new URLSearchParams({
      report_id: String(report_id),
      page: String(page),
      size: String(WEB_SEARCH_RESULTS_PAGE_SIZE),
    });
    const response = await requestApi.get(
      `${API_URL}/360/web_search/results?${params.toString()}`
    );

    if (Array.isArray(response.data)) {
      results.push(...response.data);

      if (response.data.length === 0) return results;
      if (response.data.length < WEB_SEARCH_RESULTS_PAGE_SIZE) return results;

      page += 1;
      continue;
    }

    const pageItems = response.data.items || [];
    results.push(...pageItems);
    const total = Number(response.data.total || 0);

    if (pageItems.length === 0) return results;
    if (total > 0 && results.length >= total) return results;
    if (total <= 0 && pageItems.length < WEB_SEARCH_RESULTS_PAGE_SIZE)
      return results;

    page += 1;
  }
}

export function usePromoteWebSearchResult() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      result_external_id,
      overrides,
    }: {
      result_external_id: string;
      overrides?: {
        override_title?: string;
        override_url?: string;
        override_author?: string | null;
        override_date?: string | null;
        override_body?: string;
      };
    }): Promise<PromoteResultResponse> => {
      const response = await requestApi.post(
        `${API_URL}/360/web_search/results/${result_external_id}/promote`,
        overrides || {}
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["webSearchResults"]);
        queryClient.invalidateQueries(["webSearchJobResults"]);
      },
    }
  );
}

export function useUpdateWebSearchResultStatus() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      result_external_id,
      status,
    }: {
      result_external_id: string;
      status: "viewed" | "skipped";
    }): Promise<WebSearchResult> => {
      const response = await requestApi.put(
        `${API_URL}/360/web_search/results/${result_external_id}/status?status=${status}`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["webSearchResults"]);
        queryClient.invalidateQueries(["webSearchJobResults"]);
      },
    }
  );
}

/**
 * Cancel a web search job (mark it as failed).
 * Useful when a job is stuck and the user wants to start a new search.
 */
export function useCancelWebSearchJob() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation(
    async (
      run_external_id: string
    ): Promise<{ run_external_id: string; status: string }> => {
      const response = await requestApi.post(
        `${API_URL}/360/web_search/jobs/${run_external_id}/cancel`
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Invalidate job status queries to trigger refetch
        queryClient.invalidateQueries([
          "webSearchJobStatus",
          data.run_external_id,
        ]);
        queryClient.invalidateQueries(["latestWebSearchJobForReport"]);
      },
    }
  );
}
